import { Stack, useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { AppDrawer, DRAWER_WIDTH, GlobalAppBar } from "components";
import { createContext, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { AppBarProvider } from "../components";

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" && prop !== "isSmallScreen" })<{
  open?: boolean;
  isSmallScreen?: boolean;
}>(({ theme, open, isSmallScreen }) => ({
  flexGrow: 1,
  height: "100%",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isSmallScreen ? 0 : `${open ? DRAWER_WIDTH : 0}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export interface LayoutState {
  pageTitle: string;
  setPageTitle: (value: string) => void;
}

const defaultLayoutState: LayoutState = {
  pageTitle: "SRDaycare Hub",
  setPageTitle: () => {},
};

export const LayoutContext = createContext<LayoutState>(defaultLayoutState);

export default function Layout() {
  const [pageTitle, setPageTitle] = useState<string>(defaultLayoutState.pageTitle);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    if (!isSmallScreen) return; // do nothing on larger screens, since drawer is persistent there.
    setDrawerOpen(false);
  };

  const handleToggleDrawer = () => {
    setDrawerOpen((o) => !o);
  };

  return (
    <>
      <AppDrawer open={drawerOpen} onOpen={handleDrawerOpen} onClose={handleDrawerClose} />
      <Main open={drawerOpen} isSmallScreen={isSmallScreen}>
        <AppBarProvider>
          <Stack sx={{ height: "100%", width: "100%" }}>
            <GlobalAppBar toggleDrawer={handleToggleDrawer} title="Families" drawerOpen={drawerOpen} />
            <Stack flexGrow={1}>
              <LayoutContext.Provider value={{ pageTitle, setPageTitle }}>
                <Outlet />
              </LayoutContext.Provider>
            </Stack>
          </Stack>
        </AppBarProvider>
      </Main>
    </>
  );
}
